import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useMediaQuery, useTheme, Drawer } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getCurrentUser } from "../data/user";
import { stringAvatar } from "./Helpers";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { useTranslation } from "react-i18next";
import i18next from "../i18n.js";

import {
  AppBar as MuiAppBar,
  Divider,
  Toolbar,
  Container,
  IconButton,
  Typography,
  Grid,
  Paper,
  Menu,
  MenuList,
  MenuItem,
  ListItemText,
  ListItemIcon,
  Box,
  CssBaseline,
  Stack,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  List as MuiList,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  FormControl,
} from "@mui/material";

import {
  Menu as MenuIcon,
  LogoutSharp as LogoutSharpIcon,
  AltRoute as AltRouteIcon,
  GridOn as GridOnIcon,
  PrecisionManufacturing as PrecisionManufacturingIcon,
  InsertChartSharp as InsertChartSharpIcon,
  DatasetLinked as DatasetLinkedIcon,
  DirectionsBoat as DirectionsBoatIcon,
  CalendarToday as CalendarTodayIcon,
  Logout as LogoutIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Ballot as BallotIcon,
  ContactEmergency as ContactEmergencyIcon,
  Bookmark as BookmarkIcon,
  AutoAwesomeMosaic as AutoAwesomeMosaicIcon,
  MultipleStop as MultipleStopIcon,
  LocalGasStationOutlined as LocalGasStationOutlinedIcon,
  AccessTimeOutlined as AccessTimeOutlinedIcon,
  Close as CloseIcon,
  InfoOutlined as InfoOutlinedIcon,
  LockOutlined as LockOutlinedIcon,
} from "@mui/icons-material";

import Logo from "./Logo.tsx";
import { Vessel } from "../types/Vessel.ts";
import { getVessels } from "../data/vessels.js";
import { addVesselFilterValue } from "../data/filter.tsx";
import { selectedFilteredVessel } from "../selectors/filterSelector.js";
import { NotesSlideoutProvider } from "./Slideout/notes";

const defaultTheme = createTheme();

const MOBILE_ROUTES = [
  "/mobile/planning",
  "/mobile/gasoil",
  "/reports/weekly-report",
  "/vessel/gasoil",
];

const SESSION_EXPIRED_EVENT = "auth:session-expired";

const getTokenExpirationTime = (token: string | null): number | null => {
  if (!token) {
    return null;
  }

  try {
    const parts = token.split(".");

    if (parts.length !== 3) {
      return null;
    }

    const normalizedPayload = parts[1]
      .replace(/-/g, "+")
      .replace(/_/g, "/");
    const paddedPayload = normalizedPayload.padEnd(
      Math.ceil(normalizedPayload.length / 4) * 4,
      "="
    );
    const payload = JSON.parse(window.atob(paddedPayload));
    const expiration = Number(payload?.exp);

    return Number.isFinite(expiration) ? expiration * 1000 : null;
  } catch {
    return null;
  }
};

const hasValidSessionToken = () => {
  const expirationTime = getTokenExpirationTime(
    localStorage.getItem("token")
  );

  return expirationTime !== null && expirationTime > Date.now();
};

export default function Layout({
  children,
  mobileTitle,
  mobileSubtitle,
  mobileAction,
  hideMobileVesselButton = false,
  showNotesButton = false,
  notesSlideoutOverlay = true,
}: any) {
  const dispatch = useDispatch();
  const location = useLocation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [vesselSelectOpen, setVesselSelectOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [userName, setUserName] = useState("");
  const [showWarning, setShowWarning] = useState(false);
  const [userMenu, setUserMenu] = useState<HTMLElement | null>(null);
  const [tokenDialogOpen, setTokenDialogOpen] = useState(false);

  const checkNavigator = (userAgent?: string) => {
    if (!userAgent) {
      return false;
    }

    return userAgent.includes("Android") || userAgent.includes("iPhone");
  };

  const [width, setWidth] = useState<number>(window.innerWidth ?? 1);

  const handleWindowSizeChange = () => {
    setWidth(window.innerWidth);
  };

  const isMobile =
    width <= 768 || checkNavigator(window.navigator.userAgent);

  const selectedVessel = useSelector(selectedFilteredVessel);

  const {
    t,
    i18n: { changeLanguage, language },
  } = useTranslation();

  const selectedData = useSelector((state: any) => ({
    user: state.user.data,
    vessels: state.vessels.data,
  }));

  const { user, vessels } = selectedData;

  useEffect(() => {
    window.addEventListener("resize", handleWindowSizeChange);

    if (user) {
      setUserName(`${user?.lastName ?? ""} ${user?.firstName ?? ""}`);
      changeLanguage(user.defaultLanguage);
    }

    return () => {
      window.removeEventListener("resize", handleWindowSizeChange);
    };
  }, [user, location.pathname]);

  useEffect(() => {
    let expirationTimeout: number | undefined;

    const checkSession = () => {
      const expirationTime = getTokenExpirationTime(
        localStorage.getItem("token")
      );
      const valid =
        expirationTime !== null && expirationTime > Date.now();

      setTokenDialogOpen(!valid);

      if (expirationTimeout !== undefined) {
        window.clearTimeout(expirationTimeout);
        expirationTimeout = undefined;
      }

      if (valid && expirationTime !== null) {
        expirationTimeout = window.setTimeout(
          checkSession,
          Math.min(expirationTime - Date.now() + 50, 2_147_483_647)
        );
      }
    };
    const handleSessionExpired = () => setTokenDialogOpen(true);
    const handleStorage = (event: StorageEvent) => {
      if (event.key === "token" || event.key === null) {
        checkSession();
      }
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkSession();
      }
    };
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error?.response?.status === 401) {
          window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
        }

        return Promise.reject(error);
      }
    );

    checkSession();

    const sessionCheckInterval = window.setInterval(checkSession, 30_000);

    window.addEventListener("storage", handleStorage);
    window.addEventListener("focus", checkSession);
    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearInterval(sessionCheckInterval);
      if (expirationTimeout !== undefined) {
        window.clearTimeout(expirationTimeout);
      }
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("focus", checkSession);
      window.removeEventListener(
        SESSION_EXPIRED_EVENT,
        handleSessionExpired
      );
      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  useEffect(() => {
    if (!hasValidSessionToken()) {
      return;
    }

    Promise.resolve(dispatch(getCurrentUser()) as any).catch(() => {
      setTokenDialogOpen(true);
    });

    if (isMobile) {
      dispatch(getVessels());
    }
  }, [dispatch, isMobile]);

  useEffect(() => {
    if (isMobile && !MOBILE_ROUTES.includes(location.pathname)) {
      setShowWarning(true);
    }
  }, [isMobile, location.pathname]);

  const handleLanguageChange = (e: SelectChangeEvent) => {
    const selectedLanguage = e.target.value as string;

    changeLanguage(selectedLanguage);
    localStorage.setItem("i18nextLng", selectedLanguage);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setUserMenu(null);
  };

  const handleUserMenuClick = (
    event: React.MouseEvent<HTMLElement>
  ) => {
    setUserMenu(event.currentTarget);
  };

  const logout = () => {
    localStorage.removeItem("token");
    window.location.href = "/";
  };

  const goToLogin = () => {
    localStorage.removeItem("token");
    window.location.href = "/";
  };

  const handleSelectVesselClickOpen = () => {
    setVesselSelectOpen(true);
  };

  const handleSelectVesselClickClose = () => {
    setVesselSelectOpen(false);
  };

  const toggleDrawer = (isOpen: boolean) => () => {
    setDrawerOpen(isOpen);
  };

  useEffect(() => {
    const openMobileNavigation = () => setDrawerOpen(true);

    window.addEventListener("open-mobile-navigation", openMobileNavigation);

    return () => {
      window.removeEventListener("open-mobile-navigation", openMobileNavigation);
    };
  }, []);

  const VesselSelection = (props: {
    onClose: () => void;
    open: boolean;
  }) => {
    const { onClose, open } = props;

    const handleClose = () => {
      onClose();
    };

    const handleSelectVesselClick = (vessel: Vessel) => {
      dispatch(addVesselFilterValue(vessel));
      handleClose();
    };

    return (
      <Dialog onClose={handleClose} open={open}>
        <DialogTitle>{t("selectVessel")}</DialogTitle>

        <MuiList sx={{ pt: 0 }}>
          {vessels?.length > 0 &&
            vessels.map((vessel: Vessel) => (
              <ListItem disableGutters key={vessel["@id"]}>
                <ListItemButton
                  onClick={() => handleSelectVesselClick(vessel)}
                >
                  <ListItemAvatar>
                    <Avatar>
                      <DirectionsBoatIcon />
                    </Avatar>
                  </ListItemAvatar>

                  <ListItemText primary={vessel.name} />
                </ListItemButton>
              </ListItem>
            ))}
        </MuiList>
      </Dialog>
    );
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <NotesSlideoutProvider
        showButton={showNotesButton}
        overlay={notesSlideoutOverlay}
      >
        <Box sx={{ display: "flex" }}>
        <CssBaseline />

        <MuiAppBar>
          <Toolbar>
            <Grid
              sx={{
                display: "flex",
                flexGrow: 1,
                alignContent: "center",
                alignItems: "center",
                gap: "20px",
              }}
            >
              {!isMobile && (
                <Typography
                  component="h1"
                  variant="h6"
                  color="inherit"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Logo />
                </Typography>
              )}

              <IconButton
                id="menu-button"
                onClick={isMobile ? toggleDrawer(true) : handleClick}
                sx={{ color: "#FFFFFF" }}
              >
                <MenuIcon />
              </IconButton>

              {isMobile && mobileTitle && (
                <Box
                  sx={{
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                    maxWidth: "48vw",
                    color: "#fff",
                    textAlign: "center",
                    pointerEvents: "none",
                  }}
                >
                  <Typography noWrap sx={{ fontSize: "0.9rem", fontWeight: 850, textTransform: "uppercase", lineHeight: 1.1 }}>
                    {mobileTitle}
                  </Typography>
                  {mobileSubtitle && (
                    <Typography noWrap sx={{ mt: 0.25, fontSize: "0.62rem", lineHeight: 1.1, opacity: 0.9 }}>
                      {mobileSubtitle}
                    </Typography>
                  )}
                </Box>
              )}

              {!isMobile && (
                <Menu
                  id="navigation-menu"
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                  MenuListProps={{
                    "aria-labelledby": "menu-button",
                  }}
                  sx={{
                    paddingTop: 0,
                    paddingBottom: 0,
                  }}
                >
                  <Paper
                    sx={{
                      width: 320,
                      height: "100%",
                      boxShadow: "none",
                    }}
                  >
                    <MenuList>
                      <MenuItem disabled>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: "bold" }}
                        >
                          {i18next.t("logistics")}
                        </Typography>
                      </MenuItem>

                      <Link
                        to="/logistics"
                        style={{
                          textDecoration: "none",
                          color: "inherit",
                        }}
                      >
                        <MenuItem onClick={handleClose} sx={{ pl: 4 }}>
                          <ListItemIcon>
                            <AltRouteIcon fontSize="small" />
                          </ListItemIcon>

                          <ListItemText>
                            {i18next.t("planningBoard")}
                          </ListItemText>
                        </MenuItem>
                      </Link>

                      <Link
                        to="/logistics/container-upload"
                        style={{
                          textDecoration: "none",
                          color: "inherit",
                        }}
                      >
                        <MenuItem onClick={handleClose} sx={{ pl: 4 }}>
                          <ListItemIcon>
                            <DatasetLinkedIcon fontSize="small" />
                          </ListItemIcon>

                          <ListItemText>
                            {i18next.t("importFiles")}
                          </ListItemText>
                        </MenuItem>
                      </Link>

                      <Link
                        to="/voyages"
                        style={{
                          textDecoration: "none",
                          color: "inherit",
                        }}
                      >
                        <MenuItem onClick={handleClose} sx={{ pl: 4 }}>
                          <ListItemIcon>
                            <MultipleStopIcon fontSize="small" />
                          </ListItemIcon>

                          <ListItemText>
                            {i18next.t("voyages")}
                          </ListItemText>
                        </MenuItem>
                      </Link>

                      <MenuItem disabled sx={{ pl: 4 }}>
                        <ListItemIcon>
                          <BallotIcon fontSize="small" />
                        </ListItemIcon>

                        <ListItemText>
                          {i18next.t("containerList")}
                        </ListItemText>
                      </MenuItem>

                      <MenuItem disabled>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: "bold" }}
                        >
                          {i18next.t("vessel")}
                        </Typography>
                      </MenuItem>

                      <Link
                        to="/container-planner"
                        style={{
                          textDecoration: "none",
                          color: "inherit",
                        }}
                      >
                        <MenuItem onClick={handleClose} sx={{ pl: 4 }}>
                          <ListItemIcon>
                            <GridOnIcon fontSize="small" />
                          </ListItemIcon>

                          <ListItemText>
                            {i18next.t("containerStacker")}
                          </ListItemText>
                        </MenuItem>
                      </Link>

                      <Link
                        to="/voyages/manage"
                        style={{
                          textDecoration: "none",
                          color: "inherit",
                        }}
                      >
                        <MenuItem onClick={handleClose} sx={{ pl: 4 }}>
                          <ListItemIcon>
                            <BallotIcon fontSize="small" />
                          </ListItemIcon>

                          <ListItemText>
                            {i18next.t(
                              "containerPlanningPerTerminalCall"
                            )}
                          </ListItemText>
                        </MenuItem>
                      </Link>

                      <Link
                        to="/vessel/gasoil"
                        style={{
                          textDecoration: "none",
                          color: "inherit",
                        }}
                      >
                        <MenuItem onClick={handleClose} sx={{ pl: 4 }}>
                          <ListItemIcon>
                            <LocalGasStationOutlinedIcon fontSize="small" />
                          </ListItemIcon>

                          <ListItemText>
                            {i18next.t("gasoil")}
                          </ListItemText>
                        </MenuItem>
                      </Link>

                      <MenuItem disabled>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: "bold" }}
                        >
                          {i18next.t("terminal")}
                        </Typography>
                      </MenuItem>

                      <MenuItem
                        onClick={handleClose}
                        sx={{ pl: 4 }}
                        disabled
                      >
                        <ListItemIcon>
                          <PrecisionManufacturingIcon fontSize="small" />
                        </ListItemIcon>

                        <ListItemText>
                          {i18next.t("terminalView")}
                        </ListItemText>
                      </MenuItem>

                      <MenuItem disabled>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: "bold" }}
                        >
                          {i18next.t("reports")}
                        </Typography>
                      </MenuItem>

                      <Link
                        to="/reports/weekly-report"
                        style={{
                          textDecoration: "none",
                          color: "inherit",
                        }}
                      >
                        <MenuItem onClick={handleClose} sx={{ pl: 4 }}>
                          <ListItemIcon>
                            <InsertChartSharpIcon fontSize="small" />
                          </ListItemIcon>

                          <ListItemText>
                            {i18next.t("weeklyReport")}
                          </ListItemText>
                        </MenuItem>
                      </Link>

                      <MenuItem
                        onClick={handleClose}
                        sx={{ pl: 4 }}
                        disabled
                      >
                        <ListItemIcon>
                          <InsertChartSharpIcon fontSize="small" />
                        </ListItemIcon>

                        <ListItemText>
                          {i18next.t("volumeReport")}
                        </ListItemText>
                      </MenuItem>

                      <Divider sx={{ my: 1 }} />

                      <MenuItem disabled>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: "bold" }}
                        >
                          {i18next.t("settings")}
                        </Typography>
                      </MenuItem>

                      <Link
                        to="/settings/contact"
                        style={{
                          textDecoration: "none",
                          color: "inherit",
                        }}
                      >
                        <MenuItem onClick={handleClose} sx={{ pl: 4 }}>
                          <ListItemIcon>
                            <ContactEmergencyIcon fontSize="small" />
                          </ListItemIcon>

                          <ListItemText>
                            {i18next.t("contact")}
                          </ListItemText>
                        </MenuItem>
                      </Link>

                      <Link
                        to="/settings/label"
                        style={{
                          textDecoration: "none",
                          color: "inherit",
                        }}
                      >
                        <MenuItem onClick={handleClose} sx={{ pl: 4 }}>
                          <ListItemIcon>
                            <BookmarkIcon fontSize="small" />
                          </ListItemIcon>

                          <ListItemText>
                            {i18next.t("labels")}
                          </ListItemText>
                        </MenuItem>
                      </Link>

                      <Link
                        to="/settings/container-type"
                        style={{
                          textDecoration: "none",
                          color: "inherit",
                        }}
                      >
                        <MenuItem onClick={handleClose} sx={{ pl: 4 }}>
                          <ListItemIcon>
                            <AutoAwesomeMosaicIcon fontSize="small" />
                          </ListItemIcon>

                          <ListItemText>
                            {i18next.t("containerType")}
                          </ListItemText>
                        </MenuItem>
                      </Link>

                      <Link
                        to="/settings/terminal"
                        style={{
                          textDecoration: "none",
                          color: "inherit",
                        }}
                      >
                        <MenuItem onClick={handleClose} sx={{ pl: 4 }}>
                          <ListItemIcon>
                            <PrecisionManufacturingIcon fontSize="small" />
                          </ListItemIcon>

                          <ListItemText>
                            {i18next.t("terminals")}
                          </ListItemText>
                        </MenuItem>
                      </Link>

                      <Link
                        to="/settings/vessel"
                        style={{
                          textDecoration: "none",
                          color: "inherit",
                        }}
                      >
                        <MenuItem onClick={handleClose} sx={{ pl: 4 }}>
                          <ListItemIcon>
                            <DirectionsBoatIcon fontSize="small" />
                          </ListItemIcon>

                          <ListItemText>
                            {i18next.t("vessels")}
                          </ListItemText>
                        </MenuItem>
                      </Link>
                    </MenuList>
                  </Paper>
                </Menu>
              )}
            </Grid>

            {!isMobile ? (
              <>
                <Grid
                  item
                  sx={{
                    display: "flex",
                    gap: "15px",
                  }}
                >
                  {userName && (
                    <Stack
                      direction="row"
                      spacing={3}
                      alignItems="center"
                    >
                      <Box
                        display="flex"
                        flexWrap="nowrap"
                        alignItems="center"
                        gap={1}
                        aria-controls="user-menu"
                        aria-haspopup="true"
                        onClick={handleUserMenuClick}
                        sx={{
                          cursor: "pointer",
                        }}
                      >
                        <Avatar
                          {...stringAvatar(userName)}
                          sx={{
                            width: 30,
                            height: 30,
                            fontSize: "1rem",
                          }}
                        />

                        <Typography>{userName}</Typography>
                      </Box>

                      <FormControl>
                        <Select
                          sx={{ color: "#DDDDDD" }}
                          value={language}
                          label="Language"
                          onChange={handleLanguageChange}
                          variant="standard"
                        >
                          <MenuItem value="en">EN</MenuItem>
                          <MenuItem value="nl">NL</MenuItem>
                          <MenuItem value="de">DE</MenuItem>
                          <MenuItem value="fr">FR</MenuItem>
                        </Select>
                      </FormControl>
                    </Stack>
                  )}
                </Grid>

                <Menu
                  id="user-menu"
                  anchorEl={userMenu}
                  keepMounted
                  open={Boolean(userMenu)}
                  onClose={handleClose}
                >
                  <MenuItem onClick={logout}>
                    <ListItemIcon>
                      <LogoutSharpIcon fontSize="small" />
                    </ListItemIcon>

                    <ListItemText>
                      {i18next.t("logout")}
                    </ListItemText>
                  </MenuItem>
                </Menu>
              </>
            ) : hideMobileVesselButton ? (
              <IconButton sx={{ color: "#fff" }} aria-label={mobileTitle || "Mobile page action"}>
                {mobileAction}
              </IconButton>
            ) : (
              <Button
                variant="outlined"
                size="small"
                onClick={handleSelectVesselClickOpen}
                sx={{
                  border: "1px solid #FFFFFF",
                  color: "#FFFFFF",
                }}
              >
                {selectedVessel?.name ?? "N/A"}&nbsp;
                <ArrowDropDownIcon fontSize="inherit" />
              </Button>
            )}
          </Toolbar>
        </MuiAppBar>

        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={toggleDrawer(false)}
        >
          <Box
            sx={{
              width: 250,
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            <Box sx={{ flexGrow: 1 }}>
              <Typography
                component="h1"
                variant="h6"
                color="inherit"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#1976D2",
                }}
              >
                <Logo />
              </Typography>

              <MenuList>
                <Link
                  to="/mobile/planning"
                  style={{
                    textDecoration: "none",
                    color: "inherit",
                  }}
                >
                  <MenuItem onClick={toggleDrawer(false)}>
                    <ListItemIcon>
                      <CalendarTodayIcon fontSize="small" />
                    </ListItemIcon>

                    <ListItemText>
                      {i18next.t("planning")}
                    </ListItemText>
                  </MenuItem>
                </Link>
                <Link
                  to="/mobile/gasoil"
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <MenuItem onClick={toggleDrawer(false)}>
                    <ListItemIcon>
                      <LocalGasStationOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{i18next.t("gasoil")}</ListItemText>
                  </MenuItem>
                </Link>
                <Link
                  to="/reports/weekly-report"
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <MenuItem onClick={toggleDrawer(false)}>
                    <ListItemIcon>
                      <InsertChartSharpIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{i18next.t("reports")}</ListItemText>
                  </MenuItem>
                </Link>
              </MenuList>
            </Box>

            <Box>
              <Divider />

              <MenuList>
                <MenuItem onClick={logout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>

                  <ListItemText>
                    {i18next.t("logout")}
                  </ListItemText>
                </MenuItem>
              </MenuList>
            </Box>
          </Box>
        </Drawer>

        <Box
          component="main"
          sx={{
            backgroundColor: (muiTheme) =>
              muiTheme.palette.mode === "light"
                ? muiTheme.palette.grey[100]
                : muiTheme.palette.grey[900],
            flexGrow: 1,
            height: "100vh",
            overflow:
              isMobile && location.pathname === "/mobile/planning"
                ? "hidden"
                : "auto",
          }}
        >
          <Toolbar />

          <Container
            sx={{
              mt: "12px",
              mb:
                isMobile && location.pathname === "/mobile/planning" ? 0 : 4,
              height:
                isMobile && location.pathname === "/mobile/planning"
                  ? "calc(100dvh - 76px)"
                  : "auto",
              overflow:
                isMobile && location.pathname === "/mobile/planning"
                  ? "hidden"
                  : "visible",
              padding: "0 !important",
              maxWidth: "none !important",
            }}
          >
            {isMobile && vessels && (
              <VesselSelection
                open={vesselSelectOpen}
                onClose={handleSelectVesselClickClose}
              />
            )}

            <main
              style={
                isMobile && location.pathname === "/mobile/planning"
                  ? { height: "100%", overflow: "hidden" }
                  : undefined
              }
            >
              {children}
            </main>
          </Container>
        </Box>
      </Box>

      <Dialog
        open={tokenDialogOpen}
        onClose={() => {}}
        disableEscapeKeyDown
        maxWidth="xs"
        fullWidth
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(15, 23, 42, 0.68)",
            backdropFilter: "blur(1.5px)",
          },
        }}
        PaperProps={{
          elevation: 24,
          sx: {
            width: "100%",
            maxWidth: 430,
            m: 2,
            borderRadius: "18px",
            overflow: "hidden",
            boxShadow: "0 28px 70px rgba(15, 23, 42, 0.32)",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            px: { xs: 2.5, sm: 4.5 },
            pt: 3.5,
            pb: 3,
            textAlign: "center",
          }}
        >
          <IconButton
            aria-label={t("goToLogin")}
            size="small"
            onClick={goToLogin}
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              color: "text.secondary",
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <Box
            sx={{
              mx: "auto",
              mb: 1.5,
              color: "#f5b817",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AccessTimeOutlinedIcon sx={{ fontSize: 82 }} />
          </Box>

          <Typography
            component="h2"
            sx={{
              fontSize: { xs: "1.35rem", sm: "1.55rem" },
              fontWeight: 850,
              color: "#202633",
            }}
          >
            {t("sessionExpired")}
          </Typography>

          <Typography
            sx={{
              mt: 1,
              mx: "auto",
              maxWidth: 320,
              fontSize: "0.88rem",
              lineHeight: 1.55,
              color: "text.secondary",
            }}
          >
            {t("sessionExpiredInactivityMessage")}
          </Typography>

          <Box
            sx={{
              mt: 2.5,
              p: 1.5,
              borderRadius: 2,
              backgroundColor: "#eef6ff",
              display: "flex",
              alignItems: "flex-start",
              gap: 1.2,
              textAlign: "left",
            }}
          >
            <InfoOutlinedIcon
              sx={{ mt: 0.1, fontSize: 20, color: "#287fd1" }}
            />
            <Box>
              <Typography
                sx={{
                  fontSize: "0.78rem",
                  fontWeight: 800,
                  color: "#263449",
                }}
              >
                {t("whatDoesThisMean")}
              </Typography>
              <Typography
                sx={{
                  mt: 0.3,
                  fontSize: "0.74rem",
                  lineHeight: 1.45,
                  color: "#53657b",
                }}
              >
                {t("sessionSecurityExplanation")}
              </Typography>
            </Box>
          </Box>

          <Button
            fullWidth
            variant="contained"
            startIcon={<LockOutlinedIcon />}
            onClick={goToLogin}
            sx={{
              mt: 2.5,
              minHeight: 44,
              borderRadius: 1.5,
              textTransform: "none",
              fontWeight: 800,
              boxShadow: "none",
              backgroundColor: "#1976d2",
              "&:hover": {
                backgroundColor: "#1565c0",
                boxShadow: "none",
              },
            }}
          >
            {t("loginAgain")}
          </Button>

          <Typography
            sx={{
              mt: 1.6,
              fontSize: "0.72rem",
              color: "text.secondary",
            }}
          >
            {t("loginTrouble")}{" "}
            <Box component="span" sx={{ color: "primary.main" }}>
              {t("checkInternetConnection")}
            </Box>
          </Typography>
        </Box>
      </Dialog>

      <Dialog
        open={showWarning}
        onClose={() => {}}
        aria-labelledby="mobile-warning-dialog"
      >
        <DialogTitle id="mobile-warning-dialog">
          {i18next.t("unsupportedPage")}
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            {i18next.t("unsupportedPageWarningMessage")}
          </DialogContentText>
        </DialogContent>

        <DialogActions
          sx={{
            flexDirection: isSmallScreen ? "column" : "row",
            alignItems: isSmallScreen ? "stretch" : "center",
            px: 3,
            pb: 2,
            gap: 1,
          }}
        >
          <Button
            fullWidth={isSmallScreen}
            onClick={() => setShowWarning(false)}
            variant="outlined"
          >
            {i18next.t("continueAnyway")}
          </Button>

          <Button
            sx={{
              marginLeft: "0 !important",
            }}
            fullWidth={isSmallScreen}
            onClick={() => {
              window.location.href = "/mobile/planning";
            }}
            variant="contained"
          >
            {i18next.t("goToMobile")}
          </Button>
        </DialogActions>
        </Dialog>
      </NotesSlideoutProvider>
    </ThemeProvider>
  );
}
